import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import CommentSection from './components/CommentSection';
import EnrollmentModal from './components/EnrollmentModal';
import LoginModal from './components/LoginModal';
import PrototypeCourseDetail from './components/PrototypeCourseDetail';
import { courseSections } from '../../mocks/courses';
import { mockComments, Comment, Reply } from '../../mocks/comments';

export default function CourseDetail() {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState<'sobre' | 'conteudo' | 'certificado'>('sobre');
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [completedModules, setCompletedModules] = useState<number[]>([]);
  const [expandedModules, setExpandedModules] = useState<number[]>([]);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [comments, setComments] = useState<Comment[]>(mockComments);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [shareToast, setShareToast] = useState(false);
  const [shareToastMsg, setShareToastMsg] = useState('Link copiado para a área de transferência!');
  const [showShareModal, setShowShareModal] = useState(false);
  const [highlightedModule, setHighlightedModule] = useState<number | null>(null);
  const moduleRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Find the course by id
  const course = courseSections
    .flatMap(section => section.courses)
    .find(c => c.id === id);

  useEffect(() => {
    if (!course) return;
    const storedUser = localStorage.getItem('courseUser');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      setUserData(user);
      const enrollmentKey = `enrollment_${id}`;
      const isEnrolledInCourse = localStorage.getItem(enrollmentKey) === 'true';
      setIsEnrolled(isEnrolledInCourse);
      if (isEnrolledInCourse) {
        const progressKey = `progress_${id}`;
        const savedProgress = localStorage.getItem(progressKey);
        if (savedProgress) {
          setCompletedModules(JSON.parse(savedProgress));
        }
      }
    }
  }, [id, course]);

  useEffect(() => {
    if (!id || !isEnrolled) return;
    const progressKey = `progress_${id}`;
    localStorage.setItem(progressKey, JSON.stringify(completedModules));
  }, [completedModules, id, isEnrolled]);

  if (!course) {
    return <div>Curso não encontrado</div>;
  }

  if (course.isPrototype) {
    return <PrototypeCourseDetail course={course} />;
  }

  const handleEnrollment = (newUserData: any) => {
    setUserData(newUserData);
    setIsEnrolled(true);
    const enrollmentKey = `enrollment_${id}`;
    localStorage.setItem(enrollmentKey, 'true');
  };

  const handleStartCourse = () => {
    if (!isEnrolled) {
      setShowEnrollmentModal(true);
    } else {
      setActiveTab('conteudo');
      setTimeout(() => {
        window.scrollTo({ top: 400, behavior: 'smooth' });
      }, 100);
    }
  };

  const handleShare = () => {
    const url = window.location.href;
    const title = course?.title ?? 'Curso';

    const showSuccess = (msg: string) => {
      setShareToastMsg(msg);
      setShareToast(true);
      setTimeout(() => setShareToast(false), 3000);
    };

    // 1. Web Share API (mobile/modern)
    if (navigator.share) {
      navigator.share({ title, url })
        .then(() => showSuccess('Conteúdo compartilhado com sucesso!'))
        .catch(() => {/* cancelado pelo usuário */});
      return;
    }

    // 2. Clipboard API
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url)
        .then(() => showSuccess('Link copiado para a área de transferência!'))
        .catch(() => copyFallback(url, showSuccess));
      return;
    }

    // 3. execCommand fallback
    copyFallback(url, showSuccess);
  };

  const copyFallback = (url: string, onSuccess: (msg: string) => void) => {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (ok) {
        onSuccess('Link copiado para a área de transferência!');
      } else {
        setShowShareModal(true);
      }
    } catch {
      setShowShareModal(true);
    }
  };

  const handleGoToModule = (moduleIndex: number) => {
    setActiveTab('conteudo');
    setHighlightedModule(moduleIndex);
    setTimeout(() => {
      const el = moduleRefs.current[moduleIndex];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      setTimeout(() => setHighlightedModule(null), 2000);
    }, 150);
  };

  const toggleModuleCompletion = (moduleIndex: number) => {
    setCompletedModules(prev =>
      prev.includes(moduleIndex)
        ? prev.filter(i => i !== moduleIndex)
        : [...prev, moduleIndex]
    );
  };

  const toggleModuleExpand = (moduleIndex: number) => {
    setExpandedModules(prev =>
      prev.includes(moduleIndex)
        ? prev.filter(i => i !== moduleIndex)
        : [...prev, moduleIndex]
    );
  };

  const handleAddComment = (moduleIndex: number, content: string, isQuestion: boolean) => {
    const newComment: Comment = {
      id: `new-${Date.now()}`,
      moduleIndex,
      courseId: id || '1',
      author: 'Você',
      avatar: 'https://public.readdy.ai/ai/img_res/8a371564ccc14dc252634284380e67c9.jpg',
      content,
      date: new Date().toISOString().split('T')[0],
      likes: 0,
      isQuestion,
      replies: [],
    };
    setComments(prev => [newComment, ...prev]);
  };

  const handleAddReply = (moduleIndex: number, commentId: string, content: string) => {
    const newReply: Reply = {
      id: `reply-${Date.now()}`,
      author: 'Você',
      avatar: 'https://readdy.ai/api/search-image?query=Default%20user%20avatar%20silhouette%2C%20minimal%20design%2C%20neutral%20gray%20background%2C%20professional%20style%2C%20clean%20simple%20icon&width=100&height=100&seq=defaultavatar&orientation=squarish',
      content,
      date: new Date().toISOString().split('T')[0],
      likes: 0,
      isInstructor: false,
    };
    setComments(prev =>
      prev.map(comment =>
        comment.id === commentId
          ? { ...comment, replies: [...comment.replies, newReply] }
          : comment
      )
    );
  };

  const handleLikeComment = (commentId: string) => {
    setComments(prev =>
      prev.map(comment =>
        comment.id === commentId
          ? { ...comment, likes: comment.likes + 1 }
          : comment
      )
    );
  };

  const handleLikeReply = (commentId: string, replyId: string) => {
    setComments(prev =>
      prev.map(comment =>
        comment.id === commentId
          ? {
              ...comment,
              replies: comment.replies.map(reply =>
                reply.id === replyId
                  ? { ...reply, likes: reply.likes + 1 }
                  : reply
              ),
            }
          : comment
      )
    );
  };

  const handleLogin = (loggedUser: any) => {
    setUserData(loggedUser);
    setIsEnrolled(true);
    const progressKey = `progress_${id}`;
    const savedProgress = localStorage.getItem(progressKey);
    if (savedProgress) {
      setCompletedModules(JSON.parse(savedProgress));
    }
    setActiveTab('conteudo');
    setTimeout(() => {
      window.scrollTo({ top: 400, behavior: 'smooth' });
    }, 200);
  };

  const progressPercentage = (completedModules.length / course.modules) * 100;

  const moduleNames: Record<number, string> = {
    0: 'Introdução ao Audiovisual Brasileiro',
    1: 'Governança e Políticas Públicas',
    2: 'Mercado e Regulação',
    3: 'Streaming e Novas Plataformas',
    4: 'Financiamento e Incentivos',
    5: 'Tendências e Futuro',
    6: 'Estudo de Casos',
    7: 'Avaliação Final',
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Share Toast */}
      {shareToast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white px-6 py-3 rounded-full shadow-xl flex items-center gap-2 animate-fade-in">
          <i className="ri-check-circle-fill text-green-400 text-lg"></i>
          <span className="text-sm font-semibold">{shareToastMsg}</span>
        </div>
      )}

      {/* Share Modal (fallback when clipboard not available) */}
      {showShareModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => setShowShareModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-[#CC1B1B]/10 rounded-lg">
                  <i className="ri-share-line text-lg text-[#CC1B1B]"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">Compartilhar curso</h3>
              </div>
              <button
                onClick={() => setShowShareModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-all cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-3">Copie o link abaixo para compartilhar este curso:</p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={window.location.href}
                onClick={(e) => (e.target as HTMLInputElement).select()}
                className="flex-1 bg-gray-50 border-2 border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#CC1B1B] cursor-text"
              />
              <button
                onClick={() => {
                  const input = document.querySelector('input[readonly]') as HTMLInputElement;
                  if (input) { input.select(); document.execCommand('copy'); }
                  setShowShareModal(false);
                  setShareToastMsg('Link copiado!');
                  setShareToast(true);
                  setTimeout(() => setShareToast(false), 3000);
                }}
                className="bg-[#CC1B1B] text-white px-4 py-3 rounded-xl font-semibold hover:bg-[#AA1111] transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-file-copy-line mr-1"></i>
                Copiar
              </button>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-100"></div>
              <span className="text-xs text-gray-400">ou compartilhe via</span>
              <div className="flex-1 h-px bg-gray-100"></div>
            </div>
            <div className="mt-3 flex gap-3 justify-center">
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`Confira este curso: ${course?.title} — ${window.location.href}`)}`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-xl text-sm font-semibold hover:bg-green-600 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-lg"></i>
                WhatsApp
              </a>
              <a
                href={`mailto:?subject=${encodeURIComponent(course?.title ?? '')}&body=${encodeURIComponent(`Confira este curso: ${window.location.href}`)}`}
                className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-xl text-sm font-semibold hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-line text-lg"></i>
                E-mail
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideoModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setShowVideoModal(false)}
        >
          <div
            className="relative bg-gray-900 rounded-2xl overflow-hidden shadow-2xl w-full max-w-3xl mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-white font-bold text-lg">{course.title}</h3>
              <button
                onClick={() => setShowVideoModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700 rounded-full transition-all cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="relative w-full" style={{ paddingTop: '56.25%' }}>
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-800">
                <div className="w-20 h-20 flex items-center justify-center bg-[#CC1B1B] rounded-full mb-6">
                  <i className="ri-play-fill text-4xl text-white ml-1"></i>
                </div>
                <p className="text-white text-lg font-semibold">{course.title}</p>
                <p className="text-gray-400 text-sm mt-2">Introdução ao curso — {course.duration} de conteúdo</p>
                <p className="text-gray-500 text-xs mt-6 max-w-sm text-center">
                  O vídeo será exibido aqui após a integração com a plataforma de streaming.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <div className="pt-24 pb-12 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          {isEnrolled ? (
            <div className="mb-8 bg-white p-6 rounded-2xl shadow-lg border-2 border-[#0099CC]/20">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Seu Progresso</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {completedModules.length} de {course.modules} módulos concluídos
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-[#0099CC]">
                    {Math.round(progressPercentage)}%
                  </div>
                  <div className="text-xs text-gray-600 mt-1">Completo</div>
                </div>
              </div>
              <div className="relative w-full h-4 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="absolute top-0 left-0 h-full bg-gradient-to-r from-[#0099CC] to-[#FFCC00] transition-all duration-500 rounded-full"
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {Array.from({ length: course.modules }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => handleGoToModule(index)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                      completedModules.includes(index)
                        ? 'bg-[#0099CC] text-white hover:bg-[#007AA3]'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <i className={completedModules.includes(index) ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'}></i>
                    <span>Módulo {index + 1}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mb-8 bg-gradient-to-r from-[#CC1B1B]/10 to-[#FFCC00]/10 p-6 rounded-2xl border-2 border-[#CC1B1B]/20">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#CC1B1B] rounded-xl">
                    <i className="ri-user-add-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Inscreva-se gratuitamente</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Cadastre-se para acompanhar seu progresso e obter certificado
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    onClick={() => setShowLoginModal(true)}
                    className="border-2 border-[#CC1B1B] text-[#CC1B1B] font-bold px-6 py-3 rounded-xl hover:bg-[#CC1B1B]/5 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-login-circle-line mr-2"></i>
                    Já tenho conta
                  </button>
                  <button
                    onClick={() => setShowEnrollmentModal(true)}
                    className="bg-gradient-to-r from-[#CC1B1B] to-[#FFCC00] text-white font-bold px-6 py-3 rounded-xl hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                  >
                    Inscrever-se agora
                    <i className="ri-arrow-right-line ml-2"></i>
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left Content */}
            <div>
              <div className="inline-block bg-gray-800 text-white px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
                {course.category}
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
                {course.title}
              </h1>
              <div className="flex gap-4 mb-8">
                <button
                  onClick={handleStartCourse}
                  className="bg-[#CC1B1B] hover:bg-[#AA1111] text-white px-8 py-3 rounded-lg font-semibold transition-all hover:shadow-lg whitespace-nowrap cursor-pointer"
                >
                  {isEnrolled ? 'Continuar curso' : 'Iniciar agora'}
                </button>
                <button
                  onClick={() => setActiveTab('sobre')}
                  className="border-2 border-[#CC1B1B] text-[#CC1B1B] hover:bg-[#CC1B1B] hover:text-white px-8 py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
                >
                  Veja folha de descrição
                </button>
              </div>
              <div className="grid grid-cols-2 gap-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#CC1B1B]/10 rounded-lg">
                    <i className="ri-computer-line text-2xl text-[#CC1B1B]"></i>
                  </div>
                  <div><div className="text-sm text-gray-600">Curso autoformativo</div></div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#CC1B1B]/10 rounded-lg">
                    <i className="ri-medal-line text-2xl text-[#CC1B1B]"></i>
                  </div>
                  <div><div className="text-sm text-gray-600">Com certificado</div></div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#CC1B1B]/10 rounded-lg">
                    <i className="ri-global-line text-2xl text-[#CC1B1B]"></i>
                  </div>
                  <div><div className="text-sm text-gray-600">Português do Brasil</div></div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#CC1B1B]/10 rounded-lg">
                    <i className="ri-time-line text-2xl text-[#CC1B1B]"></i>
                  </div>
                  <div><div className="text-sm text-gray-600">{course.duration}</div></div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#CC1B1B]/10 rounded-lg">
                    <i className="ri-book-open-line text-2xl text-[#CC1B1B]"></i>
                  </div>
                  <div><div className="text-sm text-gray-600">{course.modules} módulos</div></div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#CC1B1B]/10 rounded-lg">
                    <i className="ri-share-line text-2xl text-[#CC1B1B]"></i>
                  </div>
                  <div>
                    <button
                      onClick={handleShare}
                      className="text-sm text-[#CC1B1B] hover:underline cursor-pointer whitespace-nowrap"
                    >
                      Compartilhar
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Video */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-gray-900">
              <img
                src={course.image}
                alt={course.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <button
                  onClick={() => setShowVideoModal(true)}
                  className="w-20 h-20 flex items-center justify-center bg-white/90 hover:bg-white rounded-full transition-all hover:scale-110 cursor-pointer"
                >
                  <i className="ri-play-fill text-4xl text-[#CC1B1B] ml-1"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b-2 border-gray-100 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-8">
            {(['sobre', 'conteudo', 'certificado'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-2 font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === tab
                    ? 'text-[#CC1B1B] border-b-2 border-[#CC1B1B]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab === 'sobre' ? 'Sobre o curso' : tab === 'conteudo' ? 'Conteúdo programático' : 'Certificado'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          {activeTab === 'sobre' && (
            <div className="max-w-4xl">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Sobre o curso</h2>
              <div className="prose prose-lg text-gray-700 space-y-4">
                <p>
                  O curso aborda a gestão do setor audiovisual no Brasil, do cinema à televisão,
                  passando pelas plataformas de streaming. Apresenta dados sobre o mercado audiovisual
                  brasileiro e discute políticas públicas para o setor.
                </p>
                <p>
                  Você vai conhecer os principais marcos regulatórios, entender como funciona o
                  ecossistema audiovisual e refletir sobre os desafios e oportunidades para o
                  desenvolvimento do setor no país.
                </p>
                <p>
                  Este curso é indicado para gestores públicos, profissionais do audiovisual,
                  estudantes e todos os interessados em compreender melhor a dinâmica deste
                  importante setor da economia criativa brasileira.
                </p>
              </div>
              <div className="mt-12 bg-gradient-to-br from-[#CC1B1B]/5 to-[#FFCC00]/5 p-8 rounded-2xl border border-[#CC1B1B]/20">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">O que você vai aprender</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-[#CC1B1B] mt-1"></i>
                    <span className="text-gray-700">Compreender a estrutura e governança do setor audiovisual brasileiro</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-[#CC1B1B] mt-1"></i>
                    <span className="text-gray-700">Conhecer os principais marcos regulatórios e políticas públicas</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-[#CC1B1B] mt-1"></i>
                    <span className="text-gray-700">Analisar dados e indicadores do mercado audiovisual</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-xl text-[#CC1B1B] mt-1"></i>
                    <span className="text-gray-700">Identificar desafios e oportunidades para o desenvolvimento do setor</span>
                  </li>
                </ul>
              </div>
              {!isEnrolled && (
                <div className="mt-8 flex items-center gap-4 flex-wrap">
                  <button
                    onClick={() => setShowEnrollmentModal(true)}
                    className="bg-gradient-to-r from-[#CC1B1B] to-[#FFCC00] text-white font-bold px-8 py-4 rounded-xl hover:shadow-lg transition-all whitespace-nowrap cursor-pointer text-lg"
                  >
                    <i className="ri-user-add-line mr-2"></i>
                    Inscrever-se gratuitamente
                  </button>
                  <button
                    onClick={() => setShowLoginModal(true)}
                    className="border-2 border-[#CC1B1B] text-[#CC1B1B] font-bold px-8 py-4 rounded-xl hover:bg-[#CC1B1B]/5 transition-all whitespace-nowrap cursor-pointer text-lg"
                  >
                    <i className="ri-login-circle-line mr-2"></i>
                    Já tenho conta
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'conteudo' && (
            <div className="max-w-4xl">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Conteúdo programático</h2>
              <div className="space-y-4">
                {Array.from({ length: course.modules }).map((_, index) => {
                  const isCompleted = completedModules.includes(index);
                  const isExpanded = expandedModules.includes(index);
                  const isHighlighted = highlightedModule === index;
                  const moduleCommentCount = comments.filter(c => c.moduleIndex === index && c.courseId === id).length;
                  return (
                    <div
                      key={index}
                      ref={(el) => { moduleRefs.current[index] = el; }}
                      className={`bg-white border-2 rounded-xl overflow-hidden transition-all duration-500 ${
                        isHighlighted
                          ? 'border-[#FFCC00] ring-4 ring-[#FFCC00]/30'
                          : isCompleted
                            ? 'border-[#0099CC] bg-[#0099CC]/5'
                            : 'border-gray-100 hover:border-[#CC1B1B]/30'
                      }`}
                    >
                      <div className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 flex-1">
                            <div className={`w-12 h-12 flex items-center justify-center rounded-lg font-bold ${
                              isCompleted 
                                ? 'bg-[#0099CC] text-white' 
                                : 'bg-[#CC1B1B] text-white'
                            }`}>
                              {isCompleted ? (
                                <i className="ri-check-line text-2xl"></i>
                              ) : (
                                <span>{index + 1}</span>
                              )}
                            </div>
                            <div className="flex-1">
                              <h3 className="text-lg font-bold text-gray-900">
                                Módulo {index + 1}: {moduleNames[index] ?? `Tópico ${index + 1}`}
                              </h3>
                              <div className="flex items-center gap-4 mt-1">
                                <p className="text-sm text-gray-600">
                                  {Math.floor(parseInt(course.duration) / course.modules)}h de conteúdo
                                </p>
                                {moduleCommentCount > 0 && (
                                  <span className="text-sm text-[#0099CC]">
                                    <i className="ri-discuss-line mr-1"></i>
                                    {moduleCommentCount} {moduleCommentCount === 1 ? 'participação' : 'participações'}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => toggleModuleExpand(index)}
                              className={`px-4 py-2 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer ${
                                isExpanded
                                  ? 'bg-[#0099CC]/10 text-[#0099CC]'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              <i className="ri-discuss-line mr-2"></i>
                              Dúvidas
                            </button>
                            <button
                              onClick={() => toggleModuleCompletion(index)}
                              className={`px-6 py-2 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer ${
                                isCompleted
                                  ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  : 'bg-[#0099CC] text-white hover:bg-[#007AA3]'
                              }`}
                            >
                              {isCompleted ? 'Concluído' : 'Marcar como concluído'}
                            </button>
                          </div>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="border-t border-gray-100">
                          <CommentSection
                            comments={comments.filter(c => c.courseId === id)}
                            moduleIndex={index}
                            onAddComment={(content, isQuestion) => handleAddComment(index, content, isQuestion)}
                            onAddReply={(commentId, content) => handleAddReply(index, commentId, content)}
                            onLikeComment={handleLikeComment}
                            onLikeReply={handleLikeReply}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {completedModules.length === course.modules && (
                <div className="mt-8 bg-gradient-to-r from-[#0099CC] to-[#FFCC00] p-8 rounded-2xl text-center">
                  <div className="w-20 h-20 flex items-center justify-center bg-white rounded-full mx-auto mb-4">
                    <i className="ri-trophy-fill text-4xl text-[#FFCC00]"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">
                    Parabéns! Você concluiu todos os módulos!
                  </h3>
                  <p className="text-white/90 mb-6">
                    Agora você pode solicitar seu certificado de conclusão
                  </p>
                  <button className="bg-white text-[#0099CC] px-8 py-3 rounded-lg font-bold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer">
                    Solicitar Certificado
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'certificado' && (
            <div className="max-w-4xl">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Certificado</h2>
              <div className="bg-gradient-to-br from-[#FF6600]/5 to-[#FFCC00]/5 p-8 rounded-2xl border border-[#CC1B1B]/20">
                <div className="flex items-start gap-6">
                  <div className="w-20 h-20 flex items-center justify-center bg-[#CC1B1B] rounded-xl flex-shrink-0">
                    <i className="ri-medal-line text-4xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Certificado de conclusão</h3>
                    <p className="text-gray-700 mb-4">
                      Ao concluir todas as atividades do curso com aproveitamento mínimo de 70%, 
                      você receberá um certificado digital de conclusão emitido pela Editora LT.
                    </p>
                    <p className="text-gray-700">
                      O certificado é gratuito e pode ser compartilhado em suas redes sociais 
                      profissionais ou incluído em seu currículo.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-6 bg-white border-2 border-gray-100 rounded-xl">
                <h4 className="font-bold text-gray-900 mb-3">Requisitos para certificação:</h4>
                <ul className="space-y-2">
                  <li className="flex items-start gap-3">
                    <i className={`text-xl mt-0.5 ${completedModules.length === course.modules ? 'ri-checkbox-circle-fill text-[#0099CC]' : 'ri-checkbox-circle-line text-[#CC1B1B]'}`}></i>
                    <span className="text-gray-700">Completar todos os módulos do curso</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-xl text-[#CC1B1B] mt-0.5"></i>
                    <span className="text-gray-700">Realizar todas as atividades avaliativas</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-line text-xl text-[#CC1B1B] mt-0.5"></i>
                    <span className="text-gray-700">Obter nota mínima de 70% nas avaliações</span>
                  </li>
                </ul>
              </div>

              {/* Certificate Status */}
              <div className="mt-8">
                {!isEnrolled ? (
                  <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6 text-center">
                    <i className="ri-lock-line text-4xl text-gray-400 mb-3 block"></i>
                    <p className="text-gray-600 font-semibold mb-4">Inscreva-se no curso para acessar o certificado</p>
                    <div className="flex items-center justify-center gap-3 flex-wrap">
                      <button
                        onClick={() => setShowEnrollmentModal(true)}
                        className="bg-[#CC1B1B] text-white px-8 py-3 rounded-lg font-bold hover:bg-[#AA1111] transition-all whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-user-add-line mr-2"></i>
                        Inscrever-se gratuitamente
                      </button>
                      <button
                        onClick={() => setShowLoginModal(true)}
                        className="border-2 border-[#CC1B1B] text-[#CC1B1B] px-8 py-3 rounded-lg font-bold hover:bg-[#CC1B1B]/5 transition-all whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-login-circle-line mr-2"></i>
                        Já tenho conta
                      </button>
                    </div>
                  </div>
                ) : completedModules.length === course.modules ? (
                  <div className="bg-gradient-to-r from-[#0099CC]/10 to-[#FFCC00]/10 border-2 border-[#0099CC] rounded-xl p-6 text-center">
                    <i className="ri-trophy-fill text-4xl text-[#FFCC00] mb-3 block"></i>
                    <p className="text-gray-900 font-bold text-lg mb-4">Você completou todos os módulos! Parabéns!</p>
                    <button
                      onClick={handleShare}
                      className="bg-[#0099CC] text-white px-8 py-3 rounded-lg font-bold hover:bg-[#007AA3] transition-all whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-download-line mr-2"></i>
                      Baixar Certificado
                    </button>
                  </div>
                ) : (
                  <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6 text-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-gray-200 rounded-full mx-auto mb-3">
                      <i className="ri-time-line text-3xl text-gray-500"></i>
                    </div>
                    <p className="text-gray-700 font-semibold mb-2">
                      Você completou {completedModules.length} de {course.modules} módulos
                    </p>
                    <p className="text-gray-500 text-sm mb-4">
                      Complete todos os módulos para liberar o certificado
                    </p>
                    <button
                      onClick={() => setActiveTab('conteudo')}
                      className="bg-[#CC1B1B] text-white px-8 py-3 rounded-lg font-bold hover:bg-[#AA1111] transition-all whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-play-circle-line mr-2"></i>
                      Continuar estudando
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />

      <EnrollmentModal
        isOpen={showEnrollmentModal}
        onClose={() => setShowEnrollmentModal(false)}
        onEnroll={handleEnrollment}
        courseName={course.title}
      />

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLogin={handleLogin}
        onSwitchToEnroll={() => setShowEnrollmentModal(true)}
        courseId={id || '1'}
      />
    </div>
  );
}
